import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { Cake, Crop, Download, ArrowBack } from "@mui/icons-material";
import { ImageCropModal } from "../components/common/ImageCropModal";
import {
  MONTH_NAMES,
  MONTH_ABBR,
  parseDob,
  getOrdinal,
  getCurrentWeekDates,
  getWeekLabel,
} from "../utils/birthdayUtils";

const SectionWatermark = () => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      backgroundImage: "url(/hbdd.png)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      opacity: 0.4,
    }}
  />
);

const CelebrantTile = ({
  member,
  caption,
  overrideSrc,
  onAdjust,
  canAdjust,
}) => {
  const displaySrc = overrideSrc || member.imgSrc;
  return (
    <article className="relative overflow-hidden bg-[#e9e6df] w-full h-full group">
      {displaySrc ? (
        // A background-image div (not an <img> with object-fit) — html2canvas
        // is known to ignore object-fit/object-position on <img> tags and
        // paints the raw unstretched image instead, which is what caused the
        // "stretched, no styling" look in the exported PNG.
        <div
          data-bg-src={displaySrc}
          className="w-full h-full"
          style={{
            backgroundImage: `url(${displaySrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center 20%",
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-3xl font-bold text-[#000C66] font-aref">
            {member.name?.charAt(0)?.toUpperCase() || "?"}
          </span>
        </div>
      )}
      {canAdjust && member.imgSrc && (
        <button
          data-html2canvas-ignore="true"
          onClick={() => onAdjust(member)}
          title="Adjust crop"
          className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm"
        >
          <Crop style={{ fontSize: 14, color: "#000C66" }} />
        </button>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 px-4 pb-4 pt-10 z-10">
        {caption && (
          <span className="block text-[#FFC000] text-[11px] font-bold tracking-wider uppercase mb-1 font-abel leading-normal">
            {caption}
          </span>
        )}
        <h3
          className="celebrant-name truncate"
          style={{
            fontFamily: "Abel",
            fontWeight: 700,
            color: "#fff",
            fontSize: 18,
            margin: 0,
            lineHeight: "25px",
          }}
        >
          {member.name}
        </h3>
      </div>
    </article>
  );
};

const MASONRY_HEIGHT = 600; // px — target height; grows if tiles would otherwise clip
const MASONRY_GAP = 16; // px
// A tile needs at least pt-10 (40px) + caption line (~17px) + its mb-1
// (4px) + name line (18px * 1.4 ≈ 25px) + pb-4 (16px) ≈ 102px to fit the
// caption+name without the article's overflow-hidden clipping either.
const MIN_TILE_HEIGHT = 112; // px
// Repeating relative-height weights, purely to vary tile proportions —
// scaled down per column below so rendered heights sum to MASONRY_HEIGHT
// when that respects MIN_TILE_HEIGHT, or grow past it otherwise.
const NATURAL_HEIGHTS = [420, 260, 340, 300, 400, 280, 360, 240, 390];

const useResponsiveCols = () => {
  const getCols = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    return w < 640 ? 1 : w < 1024 ? 2 : 3;
  };
  const [cols, setCols] = useState(getCols);
  useEffect(() => {
    const calc = () => setCols(getCols());
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return cols;
};

// Distributes items round-robin into columns, then scales each column's
// items so their heights + gaps sum to MASONRY_HEIGHT. More items in a
// column means a smaller scale factor — tiles shrink to make room instead
// of the column growing taller, but never below MIN_TILE_HEIGHT: past that
// point the column (and the section) grows instead, since a caption/name
// would otherwise get clipped by the tile's overflow-hidden.
const layoutMasonryColumns = (items, cols) => {
  const columns = Array.from({ length: cols }, () => []);
  items.forEach((item, i) => {
    columns[i % cols].push({
      item,
      naturalH: NATURAL_HEIGHTS[i % NATURAL_HEIGHTS.length],
    });
  });

  const sizedColumns = columns.map((colItems) => {
    if (colItems.length === 0) return [];
    const naturalSum = colItems.reduce((s, it) => s + it.naturalH, 0);
    const gapsTotal = (colItems.length - 1) * MASONRY_GAP;
    const scale =
      naturalSum > 0
        ? Math.max(0, (MASONRY_HEIGHT - gapsTotal) / naturalSum)
        : 1;
    return colItems.map((it) => ({
      item: it.item,
      height: Math.max(it.naturalH * scale, MIN_TILE_HEIGHT),
    }));
  });

  const containerHeight = Math.max(
    MASONRY_HEIGHT,
    ...sizedColumns.map(
      (colItems) =>
        colItems.reduce((s, it) => s + it.height, 0) +
        Math.max(colItems.length - 1, 0) * MASONRY_GAP,
    ),
  );

  return { columns: sizedColumns, containerHeight };
};

const CelebrantGrid = ({ items, renderCaption, overrides, onAdjust }) => {
  const cols = useResponsiveCols();
  const { columns, containerHeight } = useMemo(
    () => layoutMasonryColumns(items, cols),
    [items, cols],
  );

  return (
    <div
      className="flex gap-4 w-full overflow-hidden"
      style={{ height: containerHeight }}
    >
      {columns.map((colItems, ci) => (
        <div
          key={ci}
          className="flex-1 min-w-0 flex flex-col gap-4 overflow-hidden"
        >
          {colItems.map(({ item, height }) => (
            <div
              key={item._id}
              className="w-full flex-shrink-0"
              style={{ height: `${height}px` }}
            >
              <CelebrantTile
                member={item}
                caption={renderCaption?.(item)}
                overrideSrc={overrides?.[item._id]}
                onAdjust={onAdjust}
                canAdjust={!!onAdjust}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const imageToDataURL = async (url) => {
  // Cache-bust: the same URL was likely already loaded on the page via a
  // plain <img> (no CORS mode) before Download was clicked. Some browsers
  // reuse that cached opaque response for a later CORS-mode fetch, which
  // correctly fails since it was never CORS-validated. A unique query param
  // forces a genuinely fresh, properly-negotiated request every time.
  const cacheBustUrl = `${url}${url.includes("?") ? "&" : "?"}cb=${Date.now()}`;
  const res = await fetch(cacheBustUrl, { mode: "cors", cache: "no-store" });
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const downloadSection = async (ref, filename) => {
  if (!ref.current) return;
  const bgEls = Array.from(ref.current.querySelectorAll("[data-bg-src]"));
  const originalBgs = bgEls.map((el) => el.style.backgroundImage);
  // html2canvas rasterizes the "Abel" webfont taller than the CSS
  // line-height box, so the name's own overflow-hidden (from Tailwind's
  // `truncate`) chops the bottom off every glyph — visible even on tiles
  // with plenty of spare height below the name, since the clip happens on
  // the name element's own box, not a parent running out of room. Lifting
  // it only for the capture avoids that without changing the live on-screen
  // truncation behavior.
  const nameEls = Array.from(ref.current.querySelectorAll(".celebrant-name"));
  const originalOverflow = nameEls.map((el) => el.style.overflow);

  try {
    // Swap every photo to an embedded data URL first — sidesteps any
    // ambiguity around browser caching/CORS timing during the live capture,
    // since a data URL never needs a network fetch to paint into the canvas.
    await Promise.all(
      bgEls.map(async (el, i) => {
        const src = el.getAttribute("data-bg-src");
        try {
          const dataUrl = await imageToDataURL(src);
          el.style.backgroundImage = `url(${dataUrl})`;
        } catch (err) {
          console.error("Could not embed image for download:", src, err);
        }
      }),
    );

    // Custom webfonts (Abel etc.) load asynchronously; without this,
    // html2canvas can rasterize text with fallback-font metrics whose
    // different line height/descent clips characters against the tile's
    // overflow-hidden bounds.
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    nameEls.forEach((el) => {
      el.style.overflow = "visible";
    });

    const canvas = await html2canvas(ref.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } catch (err) {
    console.error(err);
    toast("Failed to download image", { type: "error" });
  } finally {
    bgEls.forEach((el, i) => {
      el.style.backgroundImage = originalBgs[i];
    });
    nameEls.forEach((el, i) => {
      el.style.overflow = originalOverflow[i];
    });
  }
};

const Birthdays = () => {
  const navigate = useNavigate();
  const { monthIndex } = useParams();
  const [members, setMembers] = useState([]);
  const weeklyRef = useRef(null);
  const monthlyRef = useRef(null);
  const [downloadingWeekly, setDownloadingWeekly] = useState(false);
  const [downloadingMonthly, setDownloadingMonthly] = useState(false);
  const [overrides, setOverrides] = useState({}); // memberId -> cropped object URL
  const [adjustingMember, setAdjustingMember] = useState(null);

  const currentMonth = new Date().getMonth();
  const parsedMonth = parseInt(monthIndex, 10);
  const selectedMonth =
    Number.isInteger(parsedMonth) && parsedMonth >= 0 && parsedMonth <= 11
      ? parsedMonth
      : currentMonth;
  const isCurrentMonth = selectedMonth === currentMonth;

  const handleDownload = async (ref, filename, setDownloading) => {
    setDownloading(true);
    try {
      await downloadSection(ref, filename);
    } finally {
      setDownloading(false);
    }
  };

  const handleAdjustCropped = (blob) => {
    const url = URL.createObjectURL(blob);
    setOverrides((prev) => ({ ...prev, [adjustingMember._id]: url }));
    setAdjustingMember(null);
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "members"), orderBy("createdAt", "desc")),
        );
        setMembers(
          snap.docs
            .map((d) => ({ _id: d.id, ...d.data() }))
            .filter((m) => m.status === "approved"),
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchMembers();
  }, []);

  const weekDates = useMemo(() => getCurrentWeekDates(), []);

  const weeklyCelebrants = useMemo(() => {
    if (!isCurrentMonth) return [];
    return members
      .map((m) => ({ ...m, parsed: parseDob(m.dob) }))
      .filter((m) => m.parsed)
      .map((m) => ({
        ...m,
        weekIndex: weekDates.findIndex(
          (d) =>
            d.getDate() === m.parsed.day && d.getMonth() === m.parsed.month,
        ),
      }))
      .filter((m) => m.weekIndex !== -1)
      .sort((a, b) => a.weekIndex - b.weekIndex);
  }, [members, weekDates, isCurrentMonth]);

  const monthlyCelebrants = useMemo(() => {
    return members
      .map((m) => ({ ...m, parsed: parseDob(m.dob) }))
      .filter((m) => m.parsed && m.parsed.month === selectedMonth)
      .sort((a, b) => a.parsed.day - b.parsed.day);
  }, [members, selectedMonth]);

  return (
    <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/birthdays")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition flex-shrink-0"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div className="w-10 h-10 rounded-xl bg-[#fff7ed] flex items-center justify-center flex-shrink-0">
              <Cake style={{ color: "#ea580c", fontSize: 20 }} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">
                Birthdays
              </h1>
              <p className="text-gray-400 text-xs font-abel">
                Celebrate members this {isCurrentMonth ? "week and " : ""}month
              </p>
            </div>
          </div>
          <img
            src="/mediaLogo.PNG"
            alt="Media"
            className="h-10 w-auto object-contain"
            draggable={false}
          />
        </div>

        {/* Weekly Celebrant — only for the current month, since "this week"
            only means something relative to right now. */}
        {isCurrentMonth && (
          <div
            ref={weeklyRef}
            className="relative overflow-hidden bg-white shadow-sm p-6 sm:p-8 mb-6"
          >
            <SectionWatermark />
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <img
                    src="/mediaLogo.PNG"
                    alt="Media"
                    draggable={false}
                    className="h-9 w-auto object-contain flex-shrink-0"
                  />
                  <h2 className="text-2xl sm:text-4xl font-black text-[#000C66] font-celebrant leading-tight max-w-md">
                    {getWeekLabel(weekDates)}
                  </h2>
                </div>
                <div className="flex items-start gap-3">
                  <p className="text-gray-400 text-sm font-abel max-w-sm sm:text-right">
                    Join us in celebrating the members marking their birthday
                    this week.
                  </p>
                  <button
                    data-html2canvas-ignore="true"
                    disabled={downloadingWeekly}
                    onClick={() =>
                      handleDownload(
                        weeklyRef,
                        "weekly-celebrants.png",
                        setDownloadingWeekly,
                      )
                    }
                    className="flex items-center gap-1.5 text-xs font-abel font-semibold px-3 py-2 bg-[#000C66] text-white rounded-lg hover:bg-blue-900 transition flex-shrink-0 disabled:opacity-60"
                  >
                    {downloadingWeekly ? (
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Download style={{ fontSize: 15 }} />
                    )}
                    {downloadingWeekly ? "Preparing..." : "Download"}
                  </button>
                </div>
              </div>

              {weeklyCelebrants.length === 0 ? (
                <p className="text-gray-400 text-sm font-abel py-6 text-center">
                  No birthdays this week.
                </p>
              ) : (
                <CelebrantGrid
                  items={weeklyCelebrants}
                  renderCaption={(m) =>
                    `${getOrdinal(m.parsed.day)} ${MONTH_ABBR[m.parsed.month]}`
                  }
                  overrides={overrides}
                  onAdjust={setAdjustingMember}
                />
              )}
            </div>
          </div>
        )}

        {/* Monthly Celebrant */}
        <div
          ref={monthlyRef}
          className="relative overflow-hidden bg-white shadow-sm p-6 sm:p-8"
        >
          <SectionWatermark />
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <img
                  src="/mediaLogo.PNG"
                  draggable={false}
                  alt="Media"
                  className="h-9 w-auto object-contain flex-shrink-0"
                />
                <h2 className="text-2xl sm:text-4xl font-black text-[#000C66] font-celebrant leading-tight max-w-md">
                  {MONTH_NAMES[selectedMonth]} Celebrant
                </h2>
              </div>
              <div className="flex items-start gap-3">
                <p className="text-gray-400 text-sm font-abel max-w-sm sm:text-right">
                  Celebrating every member with a birthday this month.
                </p>
                <button
                  data-html2canvas-ignore="true"
                  disabled={downloadingMonthly}
                  onClick={() =>
                    handleDownload(
                      monthlyRef,
                      `${MONTH_NAMES[selectedMonth].toLowerCase()}-celebrants.png`,
                      setDownloadingMonthly,
                    )
                  }
                  className="flex items-center gap-1.5 text-xs font-abel font-semibold px-3 py-2 bg-[#000C66] text-white rounded-lg hover:bg-blue-900 transition flex-shrink-0 disabled:opacity-60"
                >
                  {downloadingMonthly ? (
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Download style={{ fontSize: 15 }} />
                  )}
                  {downloadingMonthly ? "Preparing..." : "Download"}
                </button>
              </div>
            </div>

            {monthlyCelebrants.length === 0 ? (
              <p className="text-gray-400 text-sm font-abel py-8 text-center">
                No birthdays this month.
              </p>
            ) : (
              <CelebrantGrid
                items={monthlyCelebrants}
                renderCaption={(m) => `${getOrdinal(m.parsed.day)}`}
                overrides={overrides}
                onAdjust={setAdjustingMember}
              />
            )}
          </div>
        </div>
      </div>

      <ImageCropModal
        open={!!adjustingMember}
        imageSrc={adjustingMember?.imgSrc}
        aspect={1}
        onCancel={() => setAdjustingMember(null)}
        onCropped={handleAdjustCropped}
      />
    </div>
  );
};

export default Birthdays;
